import React from "react";
import { Phone, Mail, MapPin, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Send className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-semibold text-primary">Message Sent!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0412 922 689" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="info@goldstarexecutivecleaning.com.au" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Your Company Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="service_interest" render={({
        field
      }) => <FormItem>
              <FormLabel>Service of Interest</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none dark:bg-input/30" {...field}>
                    <option value="" disabled selected>Select a service</option>
                    <option value="Office Cleaning">Office Cleaning</option>
                    <option value="Strata Building Cleaning">Strata Building Cleaning</option>
                    <option value="Gym Cleaning">Gym Cleaning</option>
                    <option value="Childcare Centre Cleaning">Childcare Centre Cleaning</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us more about your cleaning needs..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
};
export default function ContactFormDetails() {
  return <section id="contact-form-details" className="dark bg-background text-foreground py-16 lg:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {}
          <div className="w-full">
            <div className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 lg:p-10 shadow-2xl shadow-black/20">
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-3">Drop us a line</h2>
                <p className="text-muted-foreground">
                  Fill out the form below and our team will get back to you with a custom quote for your commercial cleaning needs.
                </p>
              </div>

              <FormProvider formId="contact_form_contact_us" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              service_interest: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! We have received your inquiry and will be in touch shortly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6 h-full justify-center">
            <div className="mb-4 lg:mb-8">
              <h3 className="text-2xl font-semibold mb-2">Get in touch</h3>
              <p className="text-muted-foreground">
                Prefer to speak directly? Reach out to our team via phone or email, or visit our head office.
              </p>
            </div>

            {}
            <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors duration-300 group">
              <CardContent className="flex items-center p-6 gap-6">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Phone className="h-7 w-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-1">Call Us</h4>
                  <p className="text-muted-foreground text-sm mb-3">Mon-Fri from 8am to 6pm</p>
                  <Link to="tel:0412922689" className="inline-block">
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary/80 font-medium text-base">
                      0412 922 689
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors duration-300 group">
              <CardContent className="flex items-center p-6 gap-6">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Mail className="h-7 w-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-1">Email Us</h4>
                  <p className="text-muted-foreground text-sm mb-3">For general inquiries & quotes</p>
                  <Link to="mailto:info@goldstarexecutivecleaning.com.au" className="inline-block">
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary/80 font-medium text-base break-all">
                      info@goldstarexecutivecleaning.com.au
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors duration-300 group">
              <CardContent className="flex items-center p-6 gap-6">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                  <MapPin className="h-7 w-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-1">Head Office</h4>
                  <p className="text-muted-foreground text-sm mb-3">Servicing Newcastle, Central Coast & Sydney</p>
                  <Link to="https://maps.google.com" newTab className="inline-block">
                    <Button variant="outline" className="h-9 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
                      Get Directions
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </section>;
}